/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { Component, Suspense, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Skeleton, SkeletonCard, SkeletonRegion } from '@components/ui/Skeleton';
import LoadErrorCard from '@components/ui/LoadErrorCard';
import {
  armReloadGuard,
  isChunkLoadError,
  releaseReloadGuard,
  reloadGuardSpent,
} from '@/lib/chunkReload';

/**
 * The one Suspense boundary for the lazily loaded route pages.
 *
 * Code-health survey `10-performance.md` #4 / `17-wasted-work.md` #5: App.tsx
 * imported every page statically, so the sign-in screen downloaded the whole
 * application. The pages are now `React.lazy` chunks, and this is where a
 * chunk that has not arrived yet is waited for. It sits BELOW Layout, as a
 * pathless layout route, so the sidebar and header stay painted while a page
 * chunk downloads; a boundary above Layout would blank the chrome on every
 * navigation. It also sits in App.tsx rather than inside Layout, so a test
 * that stubs Layout still has a boundary for the lazy pages.
 */

/**
 * What the reader sees while a page chunk downloads. Same live-region contract
 * as every other loading block (`SkeletonRegion`, survey 08 #17): one
 * `role="status"` saying "Loading page", the shapes themselves aria-hidden.
 */
function PageSkeleton() {
  return (
    <SkeletonRegion label="Loading page" className="p-8 space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-7 w-56" />
        <Skeleton className="h-4 w-80" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    </SkeletonRegion>
  );
}

interface BoundaryProps {
  children: ReactNode;
  /**
   * Changes on navigation. A boundary that has caught an error stays on its
   * error card until something resets it, and React does not reset it for a
   * route change, so the current pathname is passed in and a change clears
   * the caught error. Only the error state is reset; the children are not
   * remounted, which keeps param-only navigation (module to module) intact.
   * A change while NO error is held is a healthy navigation, and releases the
   * reload guard (see componentDidUpdate).
   */
  resetKey?: string;
}

interface BoundaryState {
  error: Error | null;
  /** An automatic reload is on its way; keep the skeleton up rather than flash the card. */
  reloading: boolean;
}

/**
 * Turns a missing page chunk (a stale tab after a deploy) into one automatic
 * reload, then a card. Anything that is not a chunk-load failure is rethrown
 * to the root ErrorBoundary in main.tsx, so a bug inside a page is reported
 * exactly as it was before the pages became lazy.
 */
export class RouteChunkBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null, reloading: false };

  static getDerivedStateFromError(error: Error): BoundaryState {
    // Read-only here: this can run twice per throw under StrictMode, and the
    // write that claims the reload belongs in componentDidCatch.
    return { error, reloading: isChunkLoadError(error) && !reloadGuardSpent() };
  }

  componentDidCatch(error: Error) {
    if (!isChunkLoadError(error)) return;
    if (armReloadGuard()) {
      window.location.reload();
      return;
    }
    // The reload was already spent (the chunk is still missing after it) or
    // storage cannot hold the guard: show the card, do not reload again.
    this.setState({ reloading: false });
  }

  componentDidUpdate(prev: BoundaryProps, prevState: BoundaryState) {
    if (prev.resetKey === this.props.resetKey) return;
    // An error that appeared in this same update was thrown by the NEW route
    // (a lazy import that had already rejected throws synchronously on the
    // next render). It is componentDidCatch's to handle; resetting it here
    // would re-render the thrower and catch it a second time, which spends
    // and then clears the guard in one commit.
    if (this.state.error !== prevState.error) return;
    if (this.state.error) {
      // A card or skeleton held from the previous route: the route changed, clear it.
      this.setState({ error: null, reloading: false });
      return;
    }
    // Navigated with no error held: the route being left did not fail (it
    // rendered, or was still loading when the user moved on), so the tab is
    // healthy and the incident that spent the reload is over. The new route's
    // own chunk is not waited for. Released here and not in componentDidMount,
    // because the reload lands on the very route that failed; if that route
    // fails again the guard must still read as spent, or a deploy broken for
    // every route would reload forever.
    releaseReloadGuard();
  }

  render() {
    const { error, reloading } = this.state;
    if (!error) return this.props.children;
    if (!isChunkLoadError(error)) throw error;
    if (reloading) return <PageSkeleton />;
    return (
      <LoadErrorCard
        title="This page could not be loaded."
        hint="The application was probably updated while this tab was open. Reloading fetches the current version; your data is unaffected."
        onRetry={() => window.location.reload()}
        className="m-8"
      />
    );
  }
}

export default function RouteOutlet() {
  const { pathname } = useLocation();
  return (
    <RouteChunkBoundary resetKey={pathname}>
      <Suspense fallback={<PageSkeleton />}>
        <Outlet />
      </Suspense>
    </RouteChunkBoundary>
  );
}
